import { useNotify } from '@globaleris/ui.library'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { SettlementBatchRequestModel } from '../../../api/Argentia/endpoints/settlementBatchs/model/settlementBatchs'
import { EnumEstadoCiclo } from '../../../api/oData/enums/enumEstadoCiclo'
import { useConfirmationDialog } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { fetchInvoiceCycleExecution } from '../../invoiceCycles/invoiceCycles.service'
import { createSettlementBatch } from '../../settlementBatchs/settlementBatchs.services'

interface UseSettlementBatchLaunchParams {
  executionCycleId: number
  invoiceCycleId?: number
  cycleName?: string
  /** Estado en el que está la ejecución al lanzar el lote (puede no haber pasado aún a Procesando) */
  originState: EnumEstadoCiclo
  /** El usuario elige seguir revisando: la pantalla debe quedar esperando que la ejecución arranque */
  onKeepReviewing: () => void
  /** La ejecución está Cerrada y no admite lotes (cambió desde otra ventana): la pantalla recarga su estado */
  onExecutionClosed?: () => void
}

/**
 * Hook con el lanzamiento de lotes de las pantallas de validación (listado y facturas).
 * Crea el lote esperando la respuesta y, solo si se creó, pregunta al usuario si quiere ir a la
 * pantalla Procesando o seguir revisando en la pantalla actual.
 *
 * @returns
 * - launchSettlementBatch: crea el lote y pregunta cómo continuar (true si el lote se creó)
 * - askHowToContinue: solo la pregunta, para lotes creados por otra vía (ej. carga masiva)
 */
export const useSettlementBatchLaunch = ({
  executionCycleId,
  invoiceCycleId,
  cycleName,
  originState,
  onKeepReviewing,
  onExecutionClosed
}: UseSettlementBatchLaunchParams) => {
  const [t] = useTranslation(['settlements', 'settlementsInvoices'])
  const { error } = useNotify()
  const history = useHistory()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  /**
   * @param extraNavigationState - Datos adicionales para la pantalla Procesando
   */
  const askHowToContinue = async (extraNavigationState?: Record<string, any>) => {
    // Con cancelResponse la promesa se rechaza al elegir "Seguir revisando"
    const goToProcessing = await getConfirmation(
      ConfirmationDialogs.INVOICE_CYCLE_BATCH_LAUNCHED,
      undefined,
      true
    )
      .then(() => true)
      .catch(() => false)

    closeConfirmation()

    if (goToProcessing) {
      history.push(NonAuthRoutes.invoiceCycleProcessing, {
        executionCycleId,
        invoiceCycleId,
        cycleName,
        originState,
        ...extraNavigationState
      })
    } else {
      onKeepReviewing()
    }
  }

  const launchSettlementBatch = async (
    request: SettlementBatchRequestModel,
    extraNavigationState?: Record<string, any>
  ): Promise<boolean> => {
    // Con la ejecución Cerrada: se comprueba antes. Si la lectura falla no se bloquea
    const executionResponse = await fetchInvoiceCycleExecution(executionCycleId)
    if (
      executionResponse.success &&
      executionResponse.data?.status === EnumEstadoCiclo.Finalizado
    ) {
      error(t('settlementsInvoices:notifications.executionNotModifiable.closed'))
      onExecutionClosed?.()
      return false
    }

    const response = await createSettlementBatch(request)

    if (!response.success) {
      error(response.error?.message ?? t('settlements:notifications.createSettlementBatch.error'))
      return false
    }

    await askHowToContinue(extraNavigationState)
    return true
  }

  return { launchSettlementBatch, askHowToContinue }
}
